<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-finance me-2"></i>{{ $t('page.hospital_kpi', 'Hospital KPI Dashboard') }}</h4>
        </div>
        <div class="d-flex gap-2 align-items-center">
          <select v-model="period" class="hms-input" @change="load" style="max-width:180px">
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="quarter">This quarter</option>
            <option value="ytd">Year to date</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="load"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <div v-if="loading" class="hms-pad text-center hms-muted">Loading…</div>

      <div v-if="!loading && periodInfo">
        <div class="hms-period-bar">
          <span><i class="mdi mdi-calendar-range me-1"></i>{{ periodInfo.start }} → {{ periodInfo.end }} <span class="hms-muted-sm">({{ periodInfo.days }} day{{ periodInfo.days===1?'':'s' }})</span></span>
          <span class="hms-muted-sm">vs {{ periodInfo.prev_start }} → {{ periodInfo.prev_end }}</span>
        </div>

        <!-- ===== CLINICAL ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-stethoscope"></i> Clinical Performance</div>
          <div class="hms-kpi-grid">
            <KpiCard label="OPD visits" :value="clinical.opd_visits" :prev="clinical.opd_visits_prev" />
            <KpiCard label="Admissions" :value="clinical.admissions" :prev="clinical.admissions_prev" />
            <KpiCard label="Bed Occupancy" :value="clinical.bor_pct" suffix="%" :target="85" :targetMode="'leq'" />
            <KpiCard label="ALOS" :value="clinical.alos_days" suffix="d" :target="5" :targetMode="'leq'" />
            <KpiCard label="ED arrivals" :value="clinical.ed_arrivals" />
            <KpiCard label="Deliveries" :value="clinical.deliveries" />
            <KpiCard label="C-section rate" :value="clinical.cs_rate" suffix="%" :target="15" :targetMode="'leq'" sub="WHO ≤15%" />
            <KpiCard label="Mortality rate" :value="clinical.mortality_rate" suffix="%" tone="warn" />
            <KpiCard label="Maternal deaths" :value="clinical.maternal_deaths || 0" tone="danger" />
            <KpiCard label="MMR / 100k" :value="clinical.mmr_per_100k || 0" tone="warn" />
            <KpiCard label="30-d readmissions" :value="clinical.readmissions_30d" tone="warn" />
            <KpiCard label="Beds (total)" :value="clinical.beds_total" />
          </div>
        </div>

        <!-- ===== OPERATIONAL ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-cog-outline"></i> Operational Efficiency</div>
          <div class="hms-kpi-grid">
            <KpiCard label="Avg wait (triage→consult)" :value="operational.avg_wait_min" suffix=" min" :target="30" :targetMode="'leq'" />
            <KpiCard label="Lab TAT" :value="operational.lab_tat_min" suffix=" min" :target="60" :targetMode="'leq'" />
            <KpiCard label="Lab pending" :value="operational.lab_pending" tone="warn" />
            <KpiCard label="Prescriptions" :value="operational.rx_total" />
            <KpiCard label="Rx dispensed" :value="operational.rx_dispensed_pct" suffix="%" :target="95" :targetMode="'geq'" />
            <KpiCard label="Stock-outs (zero)" :value="operational.stock_zero" tone="danger" />
            <KpiCard label="Below reorder" :value="operational.stock_below_reorder" tone="warn" />
            <KpiCard label="Surgeries" :value="operational.surgeries" />
            <KpiCard label="Cancellation rate" :value="operational.surgery_cancel_pct" suffix="%" :target="5" :targetMode="'leq'" />
          </div>
        </div>

        <!-- ===== FINANCIAL ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-cash-multiple"></i> Financial Health</div>
          <div class="hms-kpi-grid">
            <KpiCard label="Revenue billed" :value="financial.revenue_billed" :prev="financial.revenue_billed_prev" prefix="KES " :money="true" />
            <KpiCard label="Revenue collected" :value="financial.revenue_collected" prefix="KES " :money="true" tone="success" />
            <KpiCard label="Collection rate" :value="financial.collection_rate_pct" suffix="%" :target="80" :targetMode="'geq'" />
            <KpiCard label="Outstanding" :value="financial.outstanding_total" prefix="KES " :money="true" tone="warn" />
            <KpiCard label="Revenue / bed-day" :value="financial.revenue_per_bed_day" prefix="KES " :money="true" />
            <KpiCard label="SHA claims submitted" :value="financial.sha_claims_submitted" />
            <KpiCard label="SHA acceptance" :value="financial.sha_acceptance_pct" suffix="%" :target="90" :targetMode="'geq'" />
          </div>
        </div>

        <!-- ===== QUALITY ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-shield-check"></i> Quality & Safety</div>
          <div class="hms-kpi-grid">
            <KpiCard label="Patient feedback" :value="quality.feedback_total" />
            <KpiCard label="NPS" :value="quality.nps" :target="50" :targetMode="'geq'" tone="success" />
            <KpiCard label="Avg rating" :value="quality.avg_rating" suffix=" / 5" :target="4" :targetMode="'geq'" />
            <KpiCard label="Open complaints" :value="quality.complaints_open" tone="warn" />
            <KpiCard label="Incidents" :value="quality.incidents" tone="warn" />
            <KpiCard label="M&M conferences" :value="quality.mm_conferences" />
          </div>
        </div>

        <!-- ===== HR ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-account-group"></i> Human Resources</div>
          <div class="hms-kpi-grid">
            <KpiCard label="Staff total" :value="hr.staff_total" />
            <KpiCard label="Staff active" :value="hr.staff_active" />
            <KpiCard label="On leave today" :value="hr.on_leave_today" />
            <KpiCard label="Leave pending" :value="hr.leave_pending" tone="warn" />
            <KpiCard label="Licenses expired" :value="hr.licenses_expired" tone="danger" />
            <KpiCard label="Licenses due (60d)" :value="hr.licenses_due_soon" tone="warn" />
          </div>
        </div>

        <!-- ===== PROGRAMMES ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-book-open-page-variant"></i> Vertical Programmes</div>
          <div class="hms-kpi-grid">
            <KpiCard label="Immunization doses" :value="programmes.immunization_doses" />
            <KpiCard label="ANC visits" :value="programmes.anc_visits" />
            <KpiCard label="ANC 4+" :value="programmes.anc4_plus" />
            <KpiCard label="ART (active)" :value="programmes.art_active" />
            <KpiCard label="TB (active)" :value="programmes.tb_active" />
            <KpiCard label="TB notified" :value="programmes.tb_notified" />
            <KpiCard label="FP visits" :value="programmes.fp_visits" />
            <KpiCard label="CYP (FP)" :value="programmes.fp_cyp" />
          </div>
        </div>

        <!-- ===== TRENDS ===== -->
        <div class="hms-section">
          <div class="hms-section-head"><i class="mdi mdi-chart-line"></i> Trends</div>
          <div class="hms-grid-3">
            <TrendChart title="OPD visits per day" :data="trend.opd" color="#2563EB" />
            <TrendChart title="Admissions per day" :data="trend.admissions" color="#7C3AED" />
            <TrendChart title="Revenue per day (KES)" :data="trend.revenue" color="#16A34A" :money="true" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const KpiCard = {
  name: 'KpiCard',
  props: {
    label: String,
    value: { default: 0 },
    prev: { default: null },
    target: { default: null },
    targetMode: { default: 'geq' }, // geq = higher is better; leq = lower is better
    suffix: { default: '' },
    prefix: { default: '' },
    money: { default: false },
    sub: { default: '' },
    tone: { default: '' }   // 'success'|'warn'|'danger'
  },
  computed: {
    displayValue() {
      const v = this.value;
      if (v === null || v === undefined || v === '') return '—';
      if (this.money) {
        const n = Number(v);
        if (isNaN(n)) return v;
        if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + 'M';
        if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K';
        return n.toLocaleString();
      }
      return v;
    },
    deltaPct() {
      if (this.prev === null || this.prev === undefined) return null;
      const p = Number(this.prev), c = Number(this.value);
      if (isNaN(p) || isNaN(c) || p === 0) return null;
      return Math.round(100 * (c - p) / p);
    },
    deltaClass() {
      if (this.deltaPct === null) return '';
      const v = this.deltaPct;
      const goodWhenUp = (this.targetMode !== 'leq');
      if (v === 0) return 'st-flat';
      return ((v > 0) === goodWhenUp) ? 'st-good' : 'st-bad';
    },
    targetMet() {
      if (this.target === null || this.value === null || this.value === undefined) return null;
      const v = Number(this.value), t = Number(this.target);
      if (isNaN(v) || isNaN(t)) return null;
      return this.targetMode === 'leq' ? v <= t : v >= t;
    },
    cardTone() {
      if (this.targetMet === false) return 'tone-danger';
      if (this.targetMet === true)  return 'tone-success';
      if (this.tone === 'success')  return 'tone-success';
      if (this.tone === 'warn')     return 'tone-warn';
      if (this.tone === 'danger')   return 'tone-danger';
      return '';
    }
  },
  template: `
    <div class="hms-kpi-card" :class="cardTone">
      <div class="hms-kpi-label">{{ label }}</div>
      <div class="hms-kpi-value">{{ prefix }}{{ displayValue }}{{ suffix }}</div>
      <div class="hms-kpi-meta">
        <span v-if="deltaPct !== null" :class="deltaClass">
          <i class="mdi" :class="deltaPct > 0 ? 'mdi-arrow-up-bold' : (deltaPct < 0 ? 'mdi-arrow-down-bold' : 'mdi-minus')"></i>
          {{ Math.abs(deltaPct) }}%
        </span>
        <span v-if="target !== null && target !== undefined" class="hms-muted-sm">target {{ targetMode === 'leq' ? '≤' : '≥' }}{{ target }}{{ suffix }}</span>
        <span v-if="sub" class="hms-muted-sm">{{ sub }}</span>
      </div>
    </div>
  `
};

const TrendChart = {
  name: 'TrendChart',
  props: { title: String, data: { type: Array, default: () => [] }, color: { default: '#2563EB' }, money: { default: false } },
  computed: {
    points() {
      if (!this.data || !this.data.length) return [];
      const max = Math.max(...this.data.map(d => Number(d.v) || 0));
      const min = 0;
      const range = max - min || 1;
      const w = 280, h = 70, pad = 4;
      return this.data.map((d, i) => {
        const x = pad + (this.data.length === 1 ? 0 : (i * (w - 2*pad) / (this.data.length - 1)));
        const y = h - pad - ((Number(d.v) || 0) - min) * (h - 2*pad) / range;
        return { x, y, d: d.d, v: d.v };
      });
    },
    polyline() { return this.points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '); },
    areaPoly() {
      if (!this.points.length) return '';
      const first = this.points[0], last = this.points[this.points.length - 1];
      return `${first.x.toFixed(1)},66 ${this.polyline} ${last.x.toFixed(1)},66`;
    },
    total() {
      const s = this.data.reduce((a, b) => a + (Number(b.v) || 0), 0);
      if (this.money) {
        if (s >= 1e6) return (s / 1e6).toFixed(2) + 'M';
        if (s >= 1e3) return (s / 1e3).toFixed(1) + 'K';
      }
      return s.toLocaleString();
    },
    avg() {
      if (!this.data.length) return 0;
      const s = this.data.reduce((a, b) => a + (Number(b.v) || 0), 0);
      return Math.round(s / this.data.length);
    }
  },
  template: `
    <div class="hms-card">
      <div class="hms-card-head d-flex justify-content-between"><span>{{ title }}</span><span class="hms-muted-sm">Σ {{ total }} · avg {{ avg }}</span></div>
      <div class="hms-card-body">
        <svg v-if="data && data.length" width="100%" height="80" viewBox="0 0 288 80" preserveAspectRatio="none">
          <polygon :points="areaPoly" :fill="color" fill-opacity="0.12" />
          <polyline :points="polyline" fill="none" :stroke="color" stroke-width="2" />
          <circle v-for="(p,i) in points" :key="i" :cx="p.x" :cy="p.y" r="2" :fill="color" />
        </svg>
        <div v-else class="hms-muted text-center" style="padding:18px">No data.</div>
      </div>
    </div>
  `
};

export default {
  name: "HospitalKpi",
  components: { Navigation, KpiCard, TrendChart },
  data() {
    return {
      loading: false, period: 'month',
      periodInfo: null,
      clinical: {}, operational: {}, financial: {}, quality: {}, hr: {}, programmes: {},
      trend: { opd: [], admissions: [], revenue: [] }
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'getHospitalKpiDashboard', period: this.period }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.periodInfo  = d.Period || null;
          this.clinical    = d.Clinical || {};
          this.operational = d.Operational || {};
          this.financial   = d.Financial || {};
          this.quality     = d.Quality || {};
          this.hr          = d.Hr || {};
          this.programmes  = d.Programmes || {};
          this.trend       = d.Trend || { opd: [], admissions: [], revenue: [] };
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-period-bar { background: #ebf8ff; border: 1px solid #bee3f8; border-radius: 6px; padding: 8px 12px; margin-bottom: 14px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #2c5282; }

.hms-section { margin-bottom: 22px; }
.hms-section-head { font-weight: 700; color: #2d3748; font-size: 14px; padding-bottom: 6px; border-bottom: 2px solid #2b6cb0; margin-bottom: 10px; display: inline-block; }

.hms-kpi-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
@media (max-width: 1100px) { .hms-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 600px)  { .hms-kpi-grid { grid-template-columns: repeat(2, 1fr); } }

.hms-kpi-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #2b6cb0; border-radius: 6px; padding: 10px 12px; min-height: 88px; }
.hms-kpi-card.tone-success { border-left-color: #16a34a; background: #f0fff4; }
.hms-kpi-card.tone-warn    { border-left-color: #d69e2e; background: #fffaf0; }
.hms-kpi-card.tone-danger  { border-left-color: #dc2626; background: #fff5f5; }

.hms-kpi-label { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; font-weight: 600; }
.hms-kpi-value { font-size: 22px; font-weight: 700; color: #1a202c; margin-top: 2px; }
.hms-kpi-meta { font-size: 11px; color: #718096; margin-top: 4px; display: flex; gap: 10px; align-items: center; }

.hms-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
@media (max-width: 1000px) { .hms-grid-3 { grid-template-columns: 1fr; } }
.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; }
.hms-card-head { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; font-size: 13px; }
.hms-card-body { padding: 8px 12px; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }

.st-good { color: #16a34a; font-weight: 600; }
.st-bad  { color: #dc2626; font-weight: 600; }
.st-flat { color: #718096; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
